import {
  calculateProgress,
  calculateQuizResult,
  deriveRoute,
  getNextQuestionId,
  pruneStaleAnswers,
  toClientResultSnapshot,
  updateAnswer,
  type AnswerMap,
  type Attribution,
  type LeadPayload,
  type QuizDefinition,
  type QuizQuestion,
  type QuizResult,
  type QuizRoute,
} from "@quiz/quiz-core";
import { captureAttribution } from "../analytics/attribution";
import { safeUuid } from "../utils";

interface PersistedSession {
  quizSessionId: string;
  startedAt?: string;
  answers: AnswerMap;
  currentQuestionId?: string;
  attribution: Attribution;
  leadSubmission?: { fingerprint: string; eventId: string; timestamp: string };
}

export class QuizSession {
  private state: PersistedSession;

  constructor(private readonly quiz: QuizDefinition) {
    const key = this.storageKey();
    const stored = sessionStorage.getItem(key);
    this.state = stored
      ? (JSON.parse(stored) as PersistedSession)
      : {
          quizSessionId: safeUuid(),
          answers: {},
          currentQuestionId: quiz.startQuestionId,
          attribution: captureAttribution(`${key}:attribution`)
        };
    this.state.answers = pruneStaleAnswers(quiz, this.state.answers);
    this.save();
  }

  get id(): string {
    return this.state.quizSessionId;
  }

  get answers(): AnswerMap {
    return { ...this.state.answers };
  }

  get attribution(): Attribution {
    return { ...this.state.attribution };
  }

  markStarted(): void {
    if (!this.state.startedAt) {
      this.state.startedAt = new Date().toISOString();
      this.save();
    }
  }

  elapsedMs(): number {
    return this.state.startedAt ? Math.max(0, Date.now() - Date.parse(this.state.startedAt)) : 0;
  }

  answer(questionId: string, answerId: string): QuizRoute {
    this.state.answers = updateAnswer(this.quiz, this.state.answers, questionId, answerId);
    const next = getNextQuestionId(this.quiz, questionId, this.state.answers);
    this.state.currentQuestionId = next && next !== "__end__" ? next : undefined;
    this.save();
    return this.route();
  }

  back(): QuizQuestion | undefined {
    const route = this.route();
    const currentId = this.state.currentQuestionId ?? route.currentQuestionId ?? route.visitedQuestionIds[route.visitedQuestionIds.length - 1];
    const currentIndex = route.visitedQuestionIds.indexOf(currentId ?? "");
    const previousId = route.visitedQuestionIds[Math.max(0, currentIndex - 1)];
    if (previousId) {
      this.state.currentQuestionId = previousId;
      this.save();
    }
    return this.currentQuestion();
  }

  currentQuestion(): QuizQuestion | undefined {
    const route = this.route();
    const currentId = this.state.currentQuestionId ?? route.currentQuestionId;
    return this.quiz.questions.find((question) => question.id === currentId);
  }

  route(): QuizRoute {
    return deriveRoute(this.quiz, this.state.answers);
  }

  displayRoute(): QuizRoute {
    const route = this.route();
    if (!this.state.currentQuestionId) return route;
    return { ...route, currentQuestionId: this.state.currentQuestionId, isComplete: false };
  }

  progress(): number {
    return calculateProgress(this.quiz, this.displayRoute(), this.state.answers);
  }

  result(): QuizResult {
    return calculateQuizResult(this.quiz, this.state.answers);
  }

  reset(): void {
    sessionStorage.removeItem(this.storageKey());
    this.state = {
      quizSessionId: safeUuid(),
      answers: {},
      currentQuestionId: this.quiz.startQuestionId,
      attribution: captureAttribution(`${this.storageKey()}:attribution`)
    };
    this.save();
  }

  toLeadPayload(contact: LeadPayload["lead"], consent: LeadPayload["consent"]): LeadPayload {
    const route = this.route();
    const result = this.result();
    const answers = route.visitedQuestionIds
      .filter((questionId) => this.state.answers[questionId])
      .map((questionId) => ({ question_id: questionId, answer_id: this.state.answers[questionId] }));
    const fingerprint = JSON.stringify({ contact, consent, answers });
    if (this.state.leadSubmission?.fingerprint !== fingerprint) {
      this.state.leadSubmission = { fingerprint, eventId: safeUuid(), timestamp: new Date().toISOString() };
      this.save();
    }
    return {
      payload_version: 1,
      event_id: this.state.leadSubmission.eventId,
      quiz_session_id: this.state.quizSessionId,
      quiz_id: this.quiz.id,
      quiz_slug: this.quiz.slug,
      quiz_version: this.quiz.version,
      timestamp: this.state.leadSubmission.timestamp,
      lead: contact,
      route: {
        visited_question_ids: route.visitedQuestionIds
      },
      answers,
      client_result: toClientResultSnapshot(result),
      attribution: this.state.attribution,
      consent
    };
  }

  private save(): void {
    sessionStorage.setItem(this.storageKey(), JSON.stringify(this.state));
  }

  private storageKey(): string {
    return `quiz:${this.quiz.slug}:v${this.quiz.version}`;
  }
}
